/**
 * Has this already been fetched, recently enough?
 *
 * Every dashboard screen refetched everything it needed the moment it
 * mounted, with no memory of having just done so. The appointments screen
 * costs five requests, the agent's dashboard the same five again because it
 * shows the same lists, and the commissions page two. Clicking between the
 * three a handful of times spends sixty requests in well under a minute —
 * which is exactly the ceiling the API allows per user, so the screens
 * started answering 429 and rendering empty.
 *
 * The data behind them changes when the agent does something, and when the
 * agent does something the screen asks again on purpose. Between those
 * moments, asking again on every visit buys nothing.
 *
 * This is deliberately not a cache: it stores no data and returns no data.
 * It answers one question — "did we already ask for this, less than `ttl`
 * ago?" — and the caller decides.
 */
const askedAt = new Map();

const DEFAULT_TTL = 30_000;

/**
 * True if the caller should fetch, and records that it is about to.
 *
 * @param {string} key  what is being fetched, e.g. "agent-appointments"
 * @param {number} ttl  how long an answer stays good, in milliseconds
 */
export const shouldFetch = (key, ttl = DEFAULT_TTL) => {
  const last = askedAt.get(key);
  if (last && Date.now() - last < ttl) return false;
  askedAt.set(key, Date.now());
  return true;
};

/**
 * Forget an answer, so the next visit asks again.
 *
 * Call this after something has changed what the answer would be — accepting
 * a booking, sending an invitation — rather than letting a stale screen sit
 * there for the rest of the minute.
 */
export const invalidate = (...keys) => keys.forEach((key) => askedAt.delete(key));

/**
 * Forget every note whose key starts with this.
 *
 * A paged list is remembered per page — "wishlist:1:12", "wishlist:2:12" —
 * so removing one property has to clear all of its pages, not just the one
 * the person happens to be looking at.
 */
export const invalidatePrefix = (...prefixes) => {
  [...askedAt.keys()].forEach((key) => {
    if (prefixes.some((p) => key.startsWith(p))) askedAt.delete(key);
  });
};

/** Everything, on the way out: a different person may sign in next. */
export const forgetAll = () => askedAt.clear();

export default shouldFetch;
